import { Injectable } from '@angular/core';

type ToastKind = 'success' | 'error' | 'info' | 'warning';

const ICONS: Record<ToastKind, string> = {
  success: '<path d="M4 10.5l4 4 8-9"/>',
  error: '<path d="M5 5l10 10M15 5L5 15"/>',
  info: '<circle cx="10" cy="10" r="7"/><path d="M10 9v4M10 6.7v.1"/>',
  warning: '<path d="M10 3.5l7.5 13h-15L10 3.5z"/><path d="M10 8.5v3.5M10 14.6v.1"/>',
};

/**
 * Roomgrid toast — bottom-centered, inverted (near-black on light / light on
 * dark via --text/--bg-elev), radius 10, slide-up 0.18s. Drop-in replacement
 * for ngx-toastr's ToastrService (same success/error/info/warning signatures;
 * the optional third options argument is accepted and ignored).
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private container: HTMLElement | null = null;

  success(message?: string, title?: string, _options?: unknown): void {
    this.show('success', message, title);
  }

  error(message?: string, title?: string, _options?: unknown): void {
    this.show('error', message, title, 4500);
  }

  info(message?: string, title?: string, _options?: unknown): void {
    this.show('info', message, title);
  }

  warning(message?: string, title?: string, _options?: unknown): void {
    this.show('warning', message, title, 4000);
  }

  private show(kind: ToastKind, message?: string, title?: string, timeout = 2600): void {
    const text = [title, message].filter(Boolean).join(' — ');
    if (!text) {
      return;
    }

    const toast = document.createElement('div');
    toast.className = `rg-toast rg-toast-${kind}`;
    toast.setAttribute('role', 'status');
    toast.innerHTML =
      `<svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" ` +
      `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICONS[kind]}</svg>` +
      `<span></span>`;
    (toast.lastElementChild as HTMLElement).textContent = text;

    this.getContainer().appendChild(toast);

    const remove = () => {
      toast.classList.add('rg-toast-out');
      setTimeout(() => toast.remove(), 200);
    };
    toast.addEventListener('click', remove);
    setTimeout(remove, timeout);
  }

  private getContainer(): HTMLElement {
    if (!this.container || !document.body.contains(this.container)) {
      this.container = document.createElement('div');
      this.container.className = 'rg-toast-container';
      document.body.appendChild(this.container);
    }
    return this.container;
  }
}
